/**
 * The CAPTCHA seam for staff sign-in.
 *
 * `POST /api/admin/auth/otp/request` requires a non-empty `captchaToken` and answers 400
 * `captcha_required` without one, so this cannot simply be omitted.
 *
 * Right now there is no real challenge to solve, and this module is honest about that rather
 * than pretending otherwise:
 *
 *   - The backend's `ICaptchaVerifier` is registered in Fake mode (`AllowAllCaptchaVerifier`),
 *     which passes every challenge. Real Cloudflare Turnstile verification is scheduled with
 *     WP-B5/B18 and has not landed.
 *   - The Turnstile widget is a script from `challenges.cloudflare.com`. This origin's CSP is
 *     `script-src 'self'` with no third-party hosts, deliberately. Turning Turnstile on here is
 *     therefore not a drop-in: it needs a CSP change in `public/_headers`, agreed rather than
 *     assumed, on the one origin where a third-party script has the most to steal.
 *
 * So this returns a placeholder that satisfies the contract's shape and nothing more. It is
 * named for what it is so that nobody reading a network log mistakes it for a solved challenge.
 *
 * WHY THIS IS NOT A HOLE TODAY: the value of a CAPTCHA on this route is throttling automated
 * probing of staff addresses. That job is already done, twice over — the route is rate limited
 * per address server-side, its response is neutral (byte-identical for a staff address and a
 * stranger, so there is nothing to learn by probing), and the whole origin sits behind
 * Cloudflare Access + an IP allowlist. When WP-B18 lands, replace the body of
 * `getCaptchaToken()` with the real widget's token and update the CSP in the same change.
 */

/** The placeholder token. Named so it is unmistakable in a network log or a server log. */
const PLACEHOLDER_TOKEN = 'captcha-not-configured';

/**
 * A challenge token for the staff OTP request.
 *
 * Async by design: the real implementation will await a widget, and making callers `await` now
 * means WP-B18 changes this file only.
 */
export async function getCaptchaToken(): Promise<string> {
  return PLACEHOLDER_TOKEN;
}
